import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useConversations } from "@/hooks/useConversations";
import { Message } from "@/types/chat";

interface UploadedDocument {
  name: string;
  data?: string; // base64 for images
  type: string;
}

interface ChatContextType {
  // Message state
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  addMessage: (message: Message) => void;
  clearMessages: () => void;
  
  // Conversation state
  currentConversationId: string | null;
  setCurrentConversationId: (id: string | null) => void;
  
  // Document selection
  selectedDocumentIds: string[];
  setSelectedDocumentIds: (ids: string[]) => void;
  
  // Uploaded documents
  uploadedDocs: UploadedDocument[];
  setUploadedDocs: React.Dispatch<React.SetStateAction<UploadedDocument[]>>;
  addUploadedDoc: (doc: UploadedDocument) => void;
  removeUploadedDoc: (index: number) => void;
  clearUploadedDocs: () => void;
  
  // Loading state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  
  // Input state
  input: string;
  setInput: (input: string) => void;
  
  // RAG options
  useBeyondRAG: boolean;
  setUseBeyondRAG: (use: boolean) => void;
  
  // Conversation management
  loadConversation: (id: string) => Promise<Message[]>;
  createNewConversation: (firstMessage: string, userId: string, selectedDocs: string[]) => Promise<string | null>;
  saveMessage: (conversationId: string, role: "user" | "assistant", content: string) => Promise<void>;
  updateConversationDocuments: (conversationId: string, documentIds: string[]) => Promise<void>;
  resetConversation: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState("");
  const [useBeyondRAG, setUseBeyondRAG] = useState(false);

  // Use the conversations hook
  const {
    currentConversationId,
    setCurrentConversationId,
    selectedDocumentIds,
    setSelectedDocumentIds,
    loadConversation: loadConversationFromHook,
    createNewConversation: createConversationFromHook,
    saveMessage: saveMessageFromHook,
    updateConversationDocuments: updateDocsFromHook,
    resetConversation: resetConversationFromHook,
  } = useConversations();

  // Message management
  const addMessage = useCallback((message: Message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Uploaded document management
  const addUploadedDoc = useCallback((doc: UploadedDocument) => {
    setUploadedDocs(prev => [...prev, doc]);
  }, []);

  const removeUploadedDoc = useCallback((index: number) => {
    setUploadedDocs(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearUploadedDocs = useCallback(() => {
    setUploadedDocs([]);
  }, []);

  // Conversation management with message state sync
  const loadConversation = useCallback(async (id: string) => {
    const loadedMessages = await loadConversationFromHook(id);
    setMessages(loadedMessages);
    return loadedMessages;
  }, [loadConversationFromHook]);

  const createNewConversation = useCallback(async (
    firstMessage: string,
    userId: string,
    selectedDocs: string[]
  ) => {
    return await createConversationFromHook(firstMessage, userId, selectedDocs);
  }, [createConversationFromHook]);

  const saveMessage = useCallback(async (
    conversationId: string,
    role: "user" | "assistant",
    content: string
  ) => {
    await saveMessageFromHook(conversationId, role, content);
  }, [saveMessageFromHook]);

  const updateConversationDocuments = useCallback(async (
    conversationId: string,
    documentIds: string[]
  ) => {
    await updateDocsFromHook(conversationId, documentIds);
  }, [updateDocsFromHook]);

  const resetConversation = useCallback(() => {
    clearMessages();
    clearUploadedDocs();
    resetConversationFromHook();
  }, [clearMessages, clearUploadedDocs, resetConversationFromHook]);

  const value: ChatContextType = {
    messages,
    setMessages,
    addMessage,
    clearMessages,
    currentConversationId,
    setCurrentConversationId,
    selectedDocumentIds,
    setSelectedDocumentIds,
    uploadedDocs,
    setUploadedDocs,
    addUploadedDoc,
    removeUploadedDoc,
    clearUploadedDocs,
    isLoading,
    setIsLoading,
    input,
    setInput,
    useBeyondRAG,
    setUseBeyondRAG,
    loadConversation,
    createNewConversation,
    saveMessage,
    updateConversationDocuments,
    resetConversation,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
